import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Avatar,
  Tooltip,
  message,
  Modal,
  Typography,
  Descriptions,
  Badge,
  Form,
  InputNumber,
  Switch,
  Divider,
  List,
  Popconfirm,
  ColorPicker,
  Radio,
  Spin
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  StarOutlined,
  SettingOutlined,
  UserOutlined,
  DatabaseOutlined,
  ApiOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { subscriptionPlansService } from '../services/subscriptionPlansService';
import SubscriptionPlanDetail from './SubscriptionPlanDetail';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = Input;

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    billingPeriod: 'all'
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [createEditModalVisible, setCreateEditModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Fetch subscription plans
  const fetchPlans = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        ...params
      };

      const response = await subscriptionPlansService.getPlans(queryParams);
      if (response.success) {
        setPlans(response.data.plans || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.totalRecords || 0
        }));
      }
    } catch (error) {
      message.error(error.message || 'Failed to fetch subscription plans');
    } finally {
      setLoading(false);
    }
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }));
  };

  // View plan details
  const viewPlan = (plan) => {
    setSelectedPlan(plan);
    setDetailModalVisible(true);
  };

  // Handle row/card click to show detail view
  const handlePlanClick = (planId) => {
    setSelectedPlanId(planId);
    setShowDetailView(true);
  };

  // Handle back from detail view
  const handleBackFromDetail = () => {
    setShowDetailView(false);
    setSelectedPlanId(null);
  };

  // Create new plan
  const createPlan = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      isPopular: false,
      sortOrder: 0,
      billingPeriod: 'monthly',
      features: [],
      limits: {
        maxUsers: -1,
        maxLeads: -1,
        maxDeals: -1,
        maxStorage: -1,
        maxIntegrations: -1
      },
      metadata: {
        color: '#1890ff'
      }
    });
    setCreateEditModalVisible(true);
  };

  // Edit plan
  const editPlan = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      ...plan,
      features: plan.features || [],
      limits: {
        maxUsers: plan.limits?.maxUsers || -1,
        maxLeads: plan.limits?.maxLeads || -1,
        maxDeals: plan.limits?.maxDeals || -1,
        maxStorage: plan.limits?.maxStorage || -1,
        maxIntegrations: plan.limits?.maxIntegrations || -1
      },
      metadata: {
        color: plan.metadata?.color || '#1890ff',
        icon: plan.metadata?.icon || '',
        badge: plan.metadata?.badge || ''
      }
    });
    setCreateEditModalVisible(true);
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const planData = {
        ...values,
        features: values.features || [],
        limits: values.limits || {},
        metadata: values.metadata || {}
      };

      if (editingPlan) {
        await subscriptionPlansService.updatePlan(editingPlan._id, planData);
        message.success('Subscription plan updated successfully');
      } else {
        await subscriptionPlansService.createPlan(planData);
        message.success('Subscription plan created successfully');
      }

      setCreateEditModalVisible(false);
      fetchPlans();
    } catch (error) {
      message.error(error.message || 'Failed to save subscription plan');
    }
  };

  // Toggle plan status
  const toggleStatus = async (planId) => {
    try {
      const response = await subscriptionPlansService.togglePlanStatus(planId);
      if (response.success) {
        message.success(response.message);
        fetchPlans();
      }
    } catch (error) {
      message.error(error.message || 'Failed to update plan status');
    }
  };

  // Delete plan
  const deletePlan = async (planId, planName) => {
    confirm({
      title: 'Delete Subscription Plan',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete the plan "${planName}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await subscriptionPlansService.deletePlan(planId);
          message.success('Subscription plan deleted successfully');
          fetchPlans();
        } catch (error) {
          message.error(error.message || 'Failed to delete subscription plan');
        }
      }
    });
  };

  useEffect(() => {
    fetchPlans();
  }, [pagination.current, pagination.pageSize, filters]);

  // Calculate statistics
  const stats = {
    totalPlans: plans.length,
    activePlans: plans.filter(plan => plan.isActive).length,
    inactivePlans: plans.filter(plan => !plan.isActive).length,
    averagePrice: plans.length > 0 ? (plans.reduce((sum, plan) => sum + plan.price, 0) / plans.length).toFixed(2) : 0
  };

  // Table columns
  const columns = [
    {
      title: 'Plan',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            style={{ backgroundColor: record.metadata?.color || '#1890ff' }}
            icon={record.metadata?.icon ? <span>{record.metadata.icon}</span> : <CrownOutlined />}
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {text}
              {record.isPopular && (
                <Tag color="gold" style={{ marginLeft: 8 }}>
                  <StarOutlined /> Popular
                </Tag>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description?.substring(0, 50)}...
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <div>
          <Text strong style={{ fontSize: '16px' }}>
            ${price}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              per {record.billingPeriod}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Billing Period',
      dataIndex: 'billingPeriod',
      key: 'billingPeriod',
      render: (period) => (
        <Tag color={
          period === 'monthly' ? 'blue' : 
          period === 'yearly' ? 'green' : 
          'purple'
        }>
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </Tag>
      )
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_, record) => (
        <div>
          <div><UserOutlined /> Users: {record.limits?.maxUsers === -1 ? 'Unlimited' : record.limits?.maxUsers || 0}</div>
          <div><DatabaseOutlined /> Storage: {record.limits?.maxStorage === -1 ? 'Unlimited' : `${record.limits?.maxStorage || 0}GB`}</div>
        </div>
      )
    },
    {
      title: 'Features',
      dataIndex: 'features',
      key: 'features',
      render: (features) => (
        <div>
          {features?.slice(0, 2).map((feature, index) => (
            <Tag key={index} style={{ marginBottom: 2 }}>
              {feature.name}
            </Tag>
          ))}
          {features?.length > 2 && (
            <Tag>+{features.length - 2} more</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'error'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewPlan(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Plan">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => editPlan(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? 'Deactivate' : 'Activate'}>
            <Button
              type="text"
              icon={record.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => toggleStatus(record._id)}
            />
          </Tooltip>
          <Tooltip title="Delete Plan">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => deletePlan(record._id, record.name)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Render card view
  const renderCardView = () => (
    <Row gutter={[16, 16]}>
      {plans.map((plan) => (
        <Col xs={24} sm={12} lg={8} xl={6} key={plan._id}>
          <Card
            hoverable
            onClick={() => handlePlanClick(plan._id)}
            style={{ 
              height: '100%',
              cursor: 'pointer',
              borderColor: plan.isPopular ? '#faad14' : undefined,
              borderWidth: plan.isPopular ? 2 : 1
            }}
            cover={
              <div style={{ 
                padding: '24px', 
                textAlign: 'center', 
                background: `linear-gradient(135deg, ${plan.metadata?.color || '#1890ff'}, ${plan.metadata?.color || '#1890ff'}20)`
              }}>
                <Avatar 
                  size={64}
                  style={{ backgroundColor: plan.metadata?.color || '#1890ff' }}
                  icon={plan.metadata?.icon ? <span style={{ fontSize: '24px' }}>{plan.metadata.icon}</span> : <CrownOutlined />}
                />
                <div style={{ marginTop: 16 }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#fff' }}>
                    ${plan.price}
                  </Text>
                  <div>
                    <Text style={{ color: '#fff', opacity: 0.8 }}>
                      per {plan.billingPeriod}
                    </Text>
                  </div>
                </div>
              </div>
            }
            actions={[
              <Tooltip title="View Details">
                <EyeOutlined onClick={(e) => { e.stopPropagation(); viewPlan(plan); }} />
              </Tooltip>,
              <Tooltip title="Edit Plan">
                <EditOutlined onClick={(e) => { e.stopPropagation(); editPlan(plan); }} />
              </Tooltip>,
              <Tooltip title={plan.isActive ? 'Deactivate' : 'Activate'}>
                {plan.isActive ? 
                  <CloseCircleOutlined onClick={(e) => { e.stopPropagation(); toggleStatus(plan._id); }} /> :
                  <CheckCircleOutlined onClick={(e) => { e.stopPropagation(); toggleStatus(plan._id); }} />
                }
              </Tooltip>
            ]}
          >
            <Card.Meta
              title={
                <Space>
                  {plan.name}
                  {plan.isPopular && (
                    <Tag color="gold" size="small">
                      <StarOutlined /> Popular
                    </Tag>
                  )}
                </Space>
              }
              description={
                <div>
                  <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 12 }}>
                    {plan.description}
                  </Paragraph>
                  <div style={{ marginBottom: 8 }}>
                    <Tag color={
                      plan.billingPeriod === 'monthly' ? 'blue' : 
                      plan.billingPeriod === 'yearly' ? 'green' : 
                      'purple'
                    }>
                      <CalendarOutlined /> {plan.billingPeriod}
                    </Tag>
                    <Badge
                      status={plan.isActive ? 'success' : 'error'}
                      text={plan.isActive ? 'Active' : 'Inactive'}
                    />
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <div><UserOutlined /> Users: {plan.limits?.maxUsers === -1 ? '∞' : plan.limits?.maxUsers || 0}</div>
                    <div><DatabaseOutlined /> Storage: {plan.limits?.maxStorage === -1 ? '∞' : `${plan.limits?.maxStorage || 0}GB`}</div>
                    <div>{plan.features?.length || 0} features included</div>
                  </div>
                </div>
              }
            />
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Show detail view if selected
  if (showDetailView && selectedPlanId) {
    return (
      <SubscriptionPlanDetail 
        planId={selectedPlanId}
        onBack={handleBackFromDetail}
      />
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Subscription Plans Management
        </Title>
        <Text type="secondary">
          Create and manage subscription plans for organizations
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Plans"
              value={stats.totalPlans}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Plans"
              value={stats.activePlans}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Inactive Plans"
              value={stats.inactivePlans}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Average Price"
              value={stats.averagePrice}
              prefix={<DollarOutlined />}
              suffix="USD"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search plans..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Billing Period"
              value={filters.billingPeriod}
              onChange={(value) => setFilters(prev => ({ ...prev, billingPeriod: value }))}
            >
              <Option value="all">All Periods</Option>
              <Option value="monthly">Monthly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="lifetime">Lifetime</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Space>
              <Radio.Group 
                value={viewMode} 
                onChange={(e) => setViewMode(e.target.value)}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="list">
                  <UnorderedListOutlined /> List
                </Radio.Button>
                <Radio.Button value="cards">
                  <AppstoreOutlined /> Cards
                </Radio.Button>
              </Radio.Group>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={createPlan}
              >
                Create Plan
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Plans Display */}
      <Card>
        {viewMode === 'list' ? (
          <Table
            columns={columns}
            dataSource={plans}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} plans`
            }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
            onRow={(record) => ({
              onClick: () => handlePlanClick(record._id),
              style: { cursor: 'pointer' }
            })}
          />
        ) : (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : (
              renderCardView()
            )}
          </div>
        )}
      </Card>

      {/* Plan Detail Modal */}
      <Modal
        title="Plan Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedPlan && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Plan Name" span={2}>
                <Space>
                  {selectedPlan.name}
                  {selectedPlan.isPopular && (
                    <Tag color="gold"><StarOutlined /> Popular</Tag>
                  )}
                  <Badge
                    status={selectedPlan.isActive ? 'success' : 'error'}
                    text={selectedPlan.isActive ? 'Active' : 'Inactive'}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedPlan.description}
              </Descriptions.Item>
              <Descriptions.Item label="Price">
                ${selectedPlan.price}
              </Descriptions.Item>
              <Descriptions.Item label="Billing Period">
                {selectedPlan.billingPeriod}
              </Descriptions.Item>
              <Descriptions.Item label="Max Users">
                {selectedPlan.limits?.maxUsers === -1 ? 'Unlimited' : selectedPlan.limits?.maxUsers || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Max Storage">
                {selectedPlan.limits?.maxStorage === -1 ? 'Unlimited' : `${selectedPlan.limits?.maxStorage || 0}GB`}
              </Descriptions.Item>
              <Descriptions.Item label="Max Leads">
                {selectedPlan.limits?.maxLeads === -1 ? 'Unlimited' : selectedPlan.limits?.maxLeads || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Max Deals">
                {selectedPlan.limits?.maxDeals === -1 ? 'Unlimited' : selectedPlan.limits?.maxDeals || 0}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Features</Divider>
            <List
              dataSource={selectedPlan.features || []}
              renderItem={(feature) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    title={feature.name}
                    description={feature.description}
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>

      {/* Create/Edit Plan Modal */}
      <Modal
        title={editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
        open={createEditModalVisible}
        onCancel={() => setCreateEditModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan Name"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="e.g., Basic Plan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  addonBefore="$"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={3} placeholder="Describe this subscription plan..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="billingPeriod"
                label="Billing Period"
                rules={[{ required: true, message: 'Please select billing period' }]}
              >
                <Select>
                  <Option value="monthly">Monthly</Option>
                  <Option value="yearly">Yearly</Option>
                  <Option value="lifetime">Lifetime</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isPopular" label="Popular Plan" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Plan Limits</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={['limits', 'maxUsers']} label="Max Users (-1 for unlimited)">
                <InputNumber style={{ width: '100%' }} min={-1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={['limits', 'maxStorage']} label="Max Storage GB (-1 for unlimited)">
                <InputNumber style={{ width: '100%' }} min={-1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={['limits', 'maxLeads']} label="Max Leads (-1 for unlimited)">
                <InputNumber style={{ width: '100%' }} min={-1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={['limits', 'maxDeals']} label="Max Deals (-1 for unlimited)">
                <InputNumber style={{ width: '100%' }} min={-1} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setCreateEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
